import React, { useEffect, useState } from 'react';
import { Field, Input, Stack, Text } from '@chakra-ui/react';
import { sendVerificationCode, confirmVerificationCode } from '../../services/auth';
import { useResendCountdown } from '../../hooks/useResendCountdown';
import {
  VERIFICATION_PURPOSE,
  isValidEmail,
  mapVerificationError,
} from '../../utils/verification';
import AuthDialog from '../ui/AuthDialog';
import { FormActions, LinkButton, PrimaryButton, SecondaryButton } from '../ui/FormActions';

const COPY = {
  en: {
    title: 'Reset password',
    lead: 'Enter your registered email to receive a verification code.',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    emailError: 'Please enter a valid email address.',
    sendCode: 'Send verification code',
    sending: 'Sending…',
    codeLabel: 'Verification code',
    codePlaceholder: '6-digit code',
    codeError: 'Please enter a 6-digit verification code.',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'At least 8 characters',
    confirmPasswordLabel: 'Confirm new password',
    confirmPasswordPlaceholder: 'Re-enter new password',
    passwordMismatch: 'Passwords do not match.',
    passwordTooShort: 'Password must be at least 8 characters.',
    reset: 'Reset password',
    resetting: 'Resetting…',
    resend: 'Resend code',
    resendWait: 'Retry in {n}s',
    success: 'Password reset. Please sign in again.',
    backToLogin: 'Back to sign in',
    close: 'Close',
    sentHint: 'If this email is registered, a verification code has been sent.',
  },
  zh: {
    title: '忘记密码',
    lead: '输入已注册邮箱，我们将发送验证码以重置密码。',
    emailLabel: '邮箱',
    emailPlaceholder: 'your@email.com',
    emailError: '请输入有效的邮箱地址。',
    sendCode: '发送验证码',
    sending: '发送中…',
    codeLabel: '验证码',
    codePlaceholder: '6 位数字验证码',
    codeError: '请输入 6 位数字验证码。',
    newPasswordLabel: '新密码',
    newPasswordPlaceholder: '至少 8 位',
    confirmPasswordLabel: '确认新密码',
    confirmPasswordPlaceholder: '再次输入新密码',
    passwordMismatch: '两次输入的密码不一致。',
    passwordTooShort: '密码至少需要 8 位。',
    reset: '重置密码',
    resetting: '重置中…',
    resend: '重新发送',
    resendWait: '请在 {n}s 后重试',
    success: '密码已重置，请重新登录。',
    backToLogin: '返回登录',
    close: '关闭',
    sentHint: '若该邮箱已注册，验证码将发送至您的邮箱。',
  },
};

function ForgotPasswordModal({ open, onClose, onSuccess, lang }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { secondsLeft, canResend, start } = useResendCountdown(0);

  useEffect(() => {
    if (open) {
      setStep('email');
      setEmail('');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setEmailError('');
      setCodeError('');
      setPasswordError('');
      setActionError('');
      setSuccessMessage('');
    }
  }, [open]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setActionError('');
    setEmailError('');
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError(t.emailError);
      return;
    }
    setSending(true);
    try {
      const result = await sendVerificationCode({
        email: trimmed,
        purpose: VERIFICATION_PURPOSE.RESET_PASSWORD,
      });
      setStep('reset');
      start(result.resendAfterSeconds ?? 60);
      setSuccessMessage(t.sentHint);
    } catch (err) {
      setActionError(mapVerificationError(err?.message, lang));
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    setActionError('');
    setSending(true);
    try {
      const result = await sendVerificationCode({
        email: email.trim(),
        purpose: VERIFICATION_PURPOSE.RESET_PASSWORD,
      });
      start(result.resendAfterSeconds ?? 60);
    } catch (err) {
      setActionError(mapVerificationError(err?.message, lang));
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setActionError('');
    setCodeError('');
    setPasswordError('');

    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setCodeError(t.codeError);
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(t.passwordTooShort);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t.passwordMismatch);
      return;
    }

    setResetting(true);
    try {
      await confirmVerificationCode({
        email: email.trim(),
        purpose: VERIFICATION_PURPOSE.RESET_PASSWORD,
        code: trimmedCode,
        newPassword,
      });
      setStep('done');
      setSuccessMessage(t.success);
    } catch (err) {
      setActionError(mapVerificationError(err?.message, lang));
    } finally {
      setResetting(false);
    }
  };

  return (
    <AuthDialog
      open={open}
      onClose={onClose}
      title={t.title}
      description={step === 'email' ? t.lead : undefined}
    >
      {actionError ? (
        <Text fontSize="sm" color="red.500" mb={3}>
          {actionError}
        </Text>
      ) : null}
      {successMessage && step !== 'done' ? (
        <Text fontSize="sm" color="green.600" mb={3}>
          {successMessage}
        </Text>
      ) : null}

      {step === 'email' ? (
        <form onSubmit={handleSendCode}>
          <Field.Root required invalid={Boolean(emailError)}>
            <Field.Label>{t.emailLabel}</Field.Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (actionError) setActionError('');
              }}
              placeholder={t.emailPlaceholder}
              borderRadius="lg"
              bg={{ base: 'white', _dark: 'gray.900' }}
            />
            {emailError ? <Field.ErrorText>{emailError}</Field.ErrorText> : null}
          </Field.Root>
          <FormActions>
            <PrimaryButton disabled={sending}>{sending ? t.sending : t.sendCode}</PrimaryButton>
            <SecondaryButton onClick={onClose}>{t.close}</SecondaryButton>
          </FormActions>
        </form>
      ) : null}

      {step === 'reset' ? (
        <form onSubmit={handleReset}>
          <Text fontSize="sm" color={{ base: 'gray.600', _dark: 'gray.400' }} mb={3}>
            {email.trim()}
          </Text>
          <Stack gap={3}>
            <Field.Root invalid={Boolean(codeError)}>
              <Field.Label>{t.codeLabel}</Field.Label>
              <Input
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (codeError) setCodeError('');
                }}
                placeholder={t.codePlaceholder}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                borderRadius="lg"
                bg={{ base: 'white', _dark: 'gray.900' }}
              />
              {codeError ? <Field.ErrorText>{codeError}</Field.ErrorText> : null}
            </Field.Root>
          </Stack>

          <LinkButton mt={2} onClick={handleResend} disabled={!canResend || sending}>
            {canResend ? t.resend : t.resendWait.replace('{n}', String(secondsLeft))}
          </LinkButton>

          <Stack gap={3} mt={3}>
            <Field.Root required>
              <Field.Label>{t.newPasswordLabel}</Field.Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder={t.newPasswordPlaceholder}
                minLength={8}
                borderRadius="lg"
                bg={{ base: 'white', _dark: 'gray.900' }}
              />
            </Field.Root>
            <Field.Root required invalid={Boolean(passwordError)}>
              <Field.Label>{t.confirmPasswordLabel}</Field.Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder={t.confirmPasswordPlaceholder}
                minLength={8}
                borderRadius="lg"
                bg={{ base: 'white', _dark: 'gray.900' }}
              />
              {passwordError ? <Field.ErrorText>{passwordError}</Field.ErrorText> : null}
            </Field.Root>
          </Stack>

          <FormActions>
            <PrimaryButton disabled={resetting}>{resetting ? t.resetting : t.reset}</PrimaryButton>
            <SecondaryButton onClick={onClose}>{t.close}</SecondaryButton>
          </FormActions>
        </form>
      ) : null}

      {step === 'done' ? (
        <>
          <Text fontSize="sm" color="green.600" mb={3}>
            {successMessage}
          </Text>
          <FormActions>
            <PrimaryButton
              type="button"
              onClick={() => {
                if (typeof onSuccess === 'function') onSuccess();
              }}
            >
              {t.backToLogin}
            </PrimaryButton>
            <SecondaryButton onClick={onClose}>{t.close}</SecondaryButton>
          </FormActions>
        </>
      ) : null}
    </AuthDialog>
  );
}

export default ForgotPasswordModal;
